import React from 'react';
import {
  FaInfoCircle,
  FaMoneyBillWave,
  FaBalanceScale,
  FaTasks,
  FaCalendarAlt,
  FaUser,
  FaCheckCircle
} from 'react-icons/fa';
import { VacantePublica } from './types';
import { jobBoardViewService } from './services/jobBoardViewService';

interface JobBoardViewDetailProps {
  vacante: VacantePublica;
  onPostular: (vacante: VacantePublica) => void;
}

export const JobBoardViewDetail: React.FC<JobBoardViewDetailProps> = ({
  vacante,
  onPostular
}) => {
  const formatearFecha = (fecha: string): string => {
    return jobBoardViewService.formatearFecha(fecha);
  };
  // Dentro del componente
  const archivoGenerado = vacante.convocatoria_pdf;
  const resultadoGenerado = vacante.resultados_pdf;
  const formatearSalario = (cantidad: number): string => {
    return jobBoardViewService.formatearSalario(cantidad);
  };
  return (
    <div className="accordion-detail-content">
      {/* Información General (full width) */}
      <section className="info-general" style={{ gridColumn: '1 / -1' }}>
        <h2>
          <FaInfoCircle className="meta-icon" />
          Información General
        </h2>

        {/* Columna Izquierda */}
        <div className="column">
          <p>
            <strong>Denominación tabular:</strong>
            {' '}{vacante.denominacion_tabular}
          </p>
          <p>
            <strong>Denominación funcional:</strong>
            {' '}{vacante.denominacion_funcional}
          </p>

          <div className="funciones-list">
            <strong>Funciones principales:</strong>
            <ul>
              {vacante.principales_funciones
                .split('\n')
                .map(f => f.trim())
                .filter(f => f)
                .map((funcion, i) => (
                  <li key={i}>
                    <FaCheckCircle className="funcion-icon" />
                    <span>{funcion}</span>
                  </li>
                ))}
            </ul>
          </div>
        </div>

        {/* Columna Derecha */}
        <div className="column">
          <p>
            <strong>Escolaridad mínima:</strong>
            {' '}{vacante.escolaridad_minima}
          </p>
          <p>
            <strong>Experiencia laboral:</strong>
            {' '}{vacante.experiencia_laboral}
          </p>
          {vacante.adicional && (
            <p>
              <strong>Requisitos adicionales:</strong>
              {' '}{vacante.adicional}
            </p>
          )}
          <p>
            <strong>Capacidades técnicas:</strong>
            {' '}{vacante.capacidades_tecnicas}
          </p>
        </div>
      </section>



      {/* Información Económica & Ponderaciones (two columns) */}
      <section className="info-economica">
        <h3>
          <FaMoneyBillWave className="meta-icon" />
          Información Económica
        </h3>
        <div className="info-content">
          <p><strong>Percepción bruta:</strong> {formatearSalario(vacante.percepcion_bruta)}</p>
          <p><strong>Percepción neta:</strong> {formatearSalario(vacante.percepcion_neta)}</p>
        </div>
      </section>

      <section className="ponderaciones">
        <h3>
          <FaBalanceScale className="meta-icon" />
          Ponderaciones del Proceso
        </h3>
        <div className="info-content">
          <p><strong>Evaluación Técnica:</strong> {vacante.ponderacion_evaluacion_tecnica}%</p>
          <p><strong>Evaluación Psicométrica:</strong> {vacante.ponderacion_psicometrica}%</p>
          <p><strong>Entrevista:</strong> {vacante.ponderacion_entrevista}%</p>
        </div>
      </section>

      {/* Capacidades Requeridas (two columns) */}
      <section className="capacidades-list">
        <h3>
          <FaTasks className="meta-icon" />
          Capacidades Generales
        </h3>
        <ul>
          {vacante.capacidades_generales?.map((capacidad, index) => (
            <li key={index}>
              <span className="capacidad-nombre">{capacidad.nombre}</span>
              <span className="capacidad-nivel">({capacidad.grado_dominio})</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="capacidades-list">
        <h3>
          <FaTasks className="meta-icon" />
          Capacidades Técnicas Profesionales
        </h3>
        <ul>
          {vacante.capacidades_tecnicas_profesionales?.map((capacidad, index) => (
            <li key={index}>
              <span className="capacidad-nombre">{capacidad.nombre}</span>
              <span className="capacidad-nivel">({capacidad.grado_dominio})</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Cronograma del Proceso (full width) */}
      <section className="cronograma" style={{ gridColumn: '1 / -1' }}>
        <h3>
          <FaCalendarAlt className="meta-icon" />
          Cronograma del Proceso
        </h3>
        <div className="table-container">
          <table className="cronograma-table">
            <thead>
              <tr>
                <th>Etapa</th>
                <th>Fecha Inicio</th>
                <th>Fecha Fin</th>
                <th>Horario</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Registro</td>
                <td>{formatearFecha(vacante.fecha_registro_inicio)}</td>
                <td>{formatearFecha(vacante.fecha_registro_fin)}</td>
                <td>{vacante.hora_registro_inicio} - {vacante.hora_registro_fin}</td>
              </tr>
              <tr>
                <td>Evaluación Técnica</td>
                <td>{formatearFecha(vacante.fecha_evaluacion_tecnica_inicio)}</td>
                <td>{formatearFecha(vacante.fecha_evaluacion_tecnica_fin)}</td>
                <td>{vacante.hora_evaluacion_tecnica_inicio} - {vacante.hora_evaluacion_tecnica_fin}</td>
              </tr>
              <tr>
                <td>Evaluación Psicométrica</td>
                <td>{formatearFecha(vacante.fecha_evaluacion_psicometrica_inicio)}</td>
                <td>{formatearFecha(vacante.fecha_evaluacion_psicometrica_fin)}</td>
                <td>{vacante.hora_evaluacion_psicometrica_inicio} - {vacante.hora_evaluacion_psicometrica_fin}</td>
              </tr>
              <tr>
                <td>Verificación de Referencias</td>
                <td>{formatearFecha(vacante.fecha_verificacion_referencias_inicio)}</td>
                <td>{formatearFecha(vacante.fecha_verificacion_referencias_fin)}</td>
                <td>{vacante.hora_verificacion_referencias_inicio} - {vacante.hora_verificacion_referencias_fin}</td>
              </tr>
              <tr>
                <td>Entrevista</td>
                <td>{formatearFecha(vacante.fecha_entrevista_inicio)}</td>
                <td>{formatearFecha(vacante.fecha_entrevista_fin)}</td>
                <td>{vacante.hora_entrevista_inicio} - {vacante.hora_entrevista_fin}</td>
              </tr>
              <tr>
                <td>Resultados</td>
                <td>{formatearFecha(vacante.fecha_resultados_inicio)}</td>
                <td>{formatearFecha(vacante.fecha_resultados_fin)}</td>
                <td>{vacante.hora_resultados_inicio} - {vacante.hora_resultados_fin}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      {resultadoGenerado && (
        <section className="archivo-generado" style={{ gridColumn: '1 / -1' }}>
          <h3>
            <FaInfoCircle className="meta-icon" />
            Resultados Convocatoria
          </h3>
          <a
            href={resultadoGenerado}
            target="_blank"
            rel="noopener noreferrer"
            className="postular-btn modify-btn btn--primary btn--lg"
            style={{
              backgroundColor: (vacante.status_name === 'DESIERTA' ? '#dc3545' :
                vacante.status_name === 'CERRADA' ? '#28a745' :
                  '#007bff') + ' !important',
              borderColor: (vacante.status_name === 'DESIERTA' ? '#dc3545' :
                vacante.status_name === 'CERRADA' ? '#28a745' :
                  '#007bff') + ' !important',
              color: 'white !important'
            }}
          >
            Descargar Resultados de Convocatoria {vacante.status_name}
          </a>
        </section>
      )}


      {archivoGenerado && (
        <section className="archivo-generado" style={{ gridColumn: '1 / -1' }}>
          <h3>
            <FaInfoCircle className="meta-icon" />
            Convocatoria Generada
          </h3>
          <a
            href={archivoGenerado}
            target="_blank"
            rel="noopener noreferrer"
            className="postular-btn modify-btn  btn--primary btn--lg"
          >
            Descargar convocatoria generada
          </a>
        </section>
      )}
      {/* Botón de Postulación (full width, centered) */}
      {vacante.status_name === 'EN PROCESO' && (
        <button
          className="postular-btn modify-btn  btn--primary btn--lg"
          onClick={() => onPostular(vacante)}
          style={{ gridColumn: '1 / -1' }}
        >
          <FaUser className="meta-icon" />
          Postularme a esta vacante
        </button>

      )}


    </div>
  );
};
